'use client';

import { Content } from '@prismicio/client';
import { PrismicNextLink } from '@prismicio/next';

import { ArrowRightIcon, ChevronIcon } from '@/assets';
import { useMenuContext } from '@/context/MenuContext';
import { PopOverMenu } from '@/slices/MenuLink/PopOverMenu';
import { useState, useRef } from 'react';
import { useModalStore } from '@/store';

/**
 * Props for `MenuLink`.
 */
export type MenuLinkProps = {
  slice: Content.MenuLinkSlice;
  context: Record<string, Content.SubmenuSlice[]>;
};

/**
 * Component for "MenuLink" Slices.
 */
const MenuLink = ({ slice, context }: MenuLinkProps): JSX.Element => {
  const { isMobile, toggleSideBar, togglePopOverMenu } = useMenuContext();
  const [isHovered, setIsHovered] = useState(false);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);
  const menuItemRef = useRef<HTMLLIElement>(null);

  const showMenu = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
    if (!isMobile) {
      setIsHovered(true);
    }
  };

  const hideMenu = () => {
    hideTimeout.current = setTimeout(() => {
      if (!isMobile) {
        setIsHovered(false);
      }
    }, 200);
  };

  const { openModal } = useModalStore();
  const clickMenuLink = (e?: React.MouseEvent) => {
    if (
      slice.variation === 'bordered' &&
      (slice.primary.label === 'Contact Us' ||
        slice.primary.label === 'Get Started')
    ) {
      if (e) e.preventDefault();
      openModal();
      return;
    }
    if (slice.variation !== 'withSubMenu') {
      toggleSideBar();
    }
    if (slice.variation === 'withSubMenu' && isMobile) {
      togglePopOverMenu(slice.primary.tag!);
    }
  };

  if (isMobile && slice.variation === 'bordered') {
    return (
      <ul>
        <li
          data-slice-type={slice.slice_type}
          data-slice-variation={slice.variation}
          className="flex-col items-start justify-start text-left group flex relative h-full lg:text-base text-lg text-white font-bold border-t-4 px-4 border-t-transparent"
        >
          <PrismicNextLink
            onClick={
              slice.primary.label === 'Contact Us' ||
              slice.primary.label === 'Get Started'
                ? (e) => clickMenuLink(e)
                : clickMenuLink
            }
            field={
              slice.primary.label === 'Contact Us' ||
              slice.primary.label === 'Get Started'
                ? undefined
                : 'link' in slice.primary
                  ? slice.primary.link
                  : undefined
            }
            className="bg-[#05f273] relative rounded-[100px] shrink-0 px-6 py-3 text-center hover:opacity-90 transition-opacity"
          >
            <span className="font-manrope font-bold text-[#000000] text-[16px] text-left text-nowrap">
              {slice.primary.label}
            </span>
          </PrismicNextLink>
        </li>
      </ul>
    );
  }

  return (
    <li
      ref={menuItemRef}
      onMouseEnter={showMenu}
      onMouseLeave={hideMenu}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`${isMobile ? 'flex-col items-start justify-start text-left' : ''} ${
        isHovered && !isMobile ? 'text-green-400' : ''
      } 
                      group flex relative h-full lg:text-base text-lg text-white font-medium border-t-4 px-0 border-t-transparent`}
    >
      <PrismicNextLink
        onClick={
          slice.variation === 'bordered' &&
          (slice.primary.label === 'Contact Us' ||
            slice.primary.label === 'Get Started')
            ? (e) => clickMenuLink(e)
            : clickMenuLink
        }
        field={
          slice.variation === 'bordered' &&
          (slice.primary.label === 'Contact Us' ||
            slice.primary.label === 'Get Started')
            ? undefined
            : 'link' in slice.primary
              ? slice.primary.link
              : undefined
        }
        className={
          slice.variation === 'bordered'
            ? 'primary-button'
            : 'relative flex items-center justify-center focus:outline-none font-inter font-medium text-white text-[16px] whitespace-nowrap group'
        }
      >
        {slice.variation === 'bordered' ? (
          <>
            <ArrowRightIcon className="arrow-icon" />
            <span className="font-manrope font-bold text-black text-[16px] text-left text-nowrap">
              {slice.primary.label}
            </span>
          </>
        ) : (
          <>
            <span className="group-hover:bg-gradient-to-r from-[#00F2A5] to-[#00C2FF] group-hover:bg-clip-text group-hover:text-transparent transition-colors duration-300">
              {slice.primary.label}
            </span>
            {slice.variation === 'withSubMenu' && (
              <span
                className={`${isHovered && !isMobile ? 'rotate-180' : 'rotate-0'} ml-1 transition-transform duration-300`}
              >
                <ChevronIcon width={isMobile ? 32 : 15} />
              </span>
            )}
            <span className="absolute bottom-0 left-0 block w-full h-[2px] bg-gradient-to-r from-[#00F2A5] to-[#00C2FF] transition-all duration-300 transform scale-x-0 group-hover:scale-x-100 origin-center" />
          </>
        )}
      </PrismicNextLink>

      {slice.variation === 'withSubMenu' && (
        <PopOverMenu
          slice={slice}
          context={context as { [key: string]: Content.SubmenuSlice[] }}
          isHovered={isHovered}
          showMenu={showMenu}
          hideMenu={hideMenu}
          menuItemRef={menuItemRef}
        />
      )}
    </li>
  );
};

export default MenuLink;
